import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, } from '@angular/forms';
import { MessageService } from 'src/app/service/message.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { MessageNotificationConfig } from 'src/app/models/commission/message-notification.model';
import { ActivatedRoute } from '@angular/router';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { OnlineCalcService } from 'src/app/service/onlinecalc.service';
import { CommissionService } from 'src/app/service/commission.service';
import { CommPlan } from 'src/app/models/commission/comm-plan.model';
import { NotificationType } from 'src/app/enum/notification-type.enum';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from 'src/app/service/notification.service';
import { CalcRunCtrl } from 'src/app/models/onlinecalc/calc-run-ctrl.model';
import { OnlineCalcModel } from 'src/app/models/onlinecalc/onlinecalc.model';

interface ContextTemplate {
  contextTemplateId: string;
  templateName: string;
  messageContent: any;
  status?: number;
  createdAt: string;
}


@Component({
  selector: 'app-message-creator',
  templateUrl: './message-creator.component.html',
  styleUrls: ['./message-creator.component.css'],
})
export class MessageCreatorComponent implements OnInit {
  messageForm!: FormGroup;
  isCreateMode = false;
  commData: CommPlan[] = [];
  contextTemplates: ContextTemplate[] = [];
  showContextLibrary = false;
  commPlansList: CalcRunCtrl[] = [];
  selectedEmployeesDisplay = '';
  previewVisible = false;
  previewMessage = 'Preview: Please make sure all fields are correct. If so, please click Create to generate message.';
  successVisible = false;
  successMessage = 'Created successfully';
  requiredVisible = false;
  requiredMessage = 'Required Fields are not selected';
  isUpdateMode = false;
  editingConfigId: string | null = null;



  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: 'auto',
    minHeight: '0',
    width: '100%',
    placeholder: 'Enter text here...',
    translate: 'no',
    defaultParagraphSeparator: 'p',
    toolbarHiddenButtons: [
      ['insertImage', 'insertVideo', 'backgroundColor'],
    ],
  };

  notificationTypes = ['Simple', 'Dispute Reminder'];
  messageTypes = [
    'Broadcast to All Users',
    'Commission Plan Specific',
    'Sales Channel-Specific News',
    'User-Specific News',
  ];
  recipients: any[] = [];
  priorityLevels = [
    'Info: General Information',
    'Alert: Time-Sensitive',
    'Emergency: Critical / Urgent',
  ];
  salesChannels2: string[] = [];
  commissionPlans: Array<{ label: string; value: number }> = [];


  selectedChannels: string[] = [];
  employeeIds: string[] = [];

  ordinalOptions = ['first', 'second', 'third', 'fourth'];
  weekDays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  private extractBodyFromMessageContent(mc: any): string {
    if (!mc) return '';

    if (typeof mc === 'object') {
      return (mc.title ?? mc.body ?? mc.context ?? '').toString();
    }

    if (typeof mc === 'string') {
      try {
        const parsed = JSON.parse(mc);
        return (parsed?.body ?? parsed?.context ?? mc).toString();
      } catch {
        return mc;
      }
    }

    return '';
  }

  private normalizeEditorContent(value: any): string {
    const html = (value ?? '').toString();

    if (!html) return '';

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    const walker = document.createTreeWalker(wrapper, NodeFilter.SHOW_TEXT);
    let lastTextNode: Text | null = null;

    while (walker.nextNode()) {
      const node = walker.currentNode as Text;
      if ((node.textContent ?? '').length) {
        lastTextNode = node;
      }
    }

    if (lastTextNode) {
      lastTextNode.textContent = (lastTextNode.textContent || '')
        .replace(/\u00A0/g, ' ')
        .replace(/\s+$/, '');
    }

    return wrapper.innerHTML.trim();
  }

  private buildMessageContent(notificationType: string, contextValue: any) {
    const isDispute = notificationType === 'Dispute Reminder';

    if (isDispute) return { title: '' };

    return { title: this.normalizeEditorContent(contextValue) };
  }

  saveCurrentContextTemplate(): void {
    const ctrl = this.messageForm.get('context');
    if (!ctrl || ctrl.disabled) return;

    const body: string = (ctrl.value || '').trim();
    if (!body) return;

    const defaultName = `Template ${this.contextTemplates.length + 1}`;
    const name = window.prompt('Enter a name for this template:', defaultName);
    if (!name || !name.trim()) return;

    const payload = {
      templateName: name.trim(),
      messageContent: { title: body },
      status: 1
    };

    this.messageService.createContextTemplate(payload).subscribe({
      next: () => {
        this.openContextLibrary();
      },
      error: (e) => console.error('❌ Failed to save template', e),
    });
  }



  openContextLibrary(): void {
    this.messageService.getContextTemplates().subscribe({
      next: (rows: any[]) => {
        const list = (rows || [])
          .filter((r: any) => (r.status ?? 1) !== 3);

        this.contextTemplates = list.map((r: any) => ({
          contextTemplateId: r.contextTemplateId ?? r.id ?? r.templateId,
          templateName: r.templateName ?? r.name,
          messageContent: r.messageContent,
          status: r.status,
          createdAt: r.createdAt
        }));

        this.showContextLibrary = true;
      },
      error: (e) => console.error('❌ Failed to load templates', e),
    });
  }




  closeContextLibrary(): void {
    this.showContextLibrary = false;
  }

  applyTemplate(tpl: ContextTemplate): void {
    const ctrl = this.messageForm.get('context');
    if (!ctrl || ctrl.disabled) return;

    const body = this.extractBodyFromMessageContent(tpl.messageContent);
    ctrl.setValue(body);
    ctrl.markAsDirty();
    this.showContextLibrary = false;
  }



  deleteTemplate(tpl: ContextTemplate): void {
    const id = tpl.contextTemplateId;
    if (!id) {
      console.error('❌ Missing contextTemplateId', tpl);
      return;
    }

    this.messageService.softDeleteContextTemplate(id).subscribe({
      next: () => {
        this.contextTemplates = this.contextTemplates.filter(
          (t) => t.contextTemplateId !== id
        );
      },
      error: (e) => console.error('❌ Failed to delete template', e),
    });
  }




  targetScope = 3;
  usnSalesChannel: any;
  public empIdList: any;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private onlineCalcService: OnlineCalcService,
    private commissionService: CommissionService,
    private notificationService: NotificationService, private httpService: HttpClient,

  ) { }

  private setControlEnabled(
    ctrl: AbstractControl | null,
    enabled: boolean
  ): void {
    if (!ctrl) return;
    const isDisabled = ctrl.disabled;
    if (enabled && isDisabled) ctrl.enable({ emitEvent: false });
    if (!enabled && !isDisabled) ctrl.disable({ emitEvent: false });
  }

  private getEndOfMonthString(): string {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return lastDay.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private getNextMonthName(): string {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return next.toLocaleDateString('en-US', { month: 'long' });
  }


  private buildCronExpression(f: any): string | null {
    if (!f.patternChoice) return null;

    const isDispute = f.notificationType === 'Dispute Reminder';

    const [hour, minute] = f.startTime
      ? f.startTime.split(':').map((x: string) => Number(x))
      : (isDispute ? [0, 0] : [9, 0]);

    if (f.patternChoice === 'day') {
      const day = f.patternDay || 1;
      const monthInterval = f.patternMonthInterval || 1;
      return `${minute} ${hour} ${day} 1/${monthInterval} ?`;
    }


    if (f.patternChoice === 'ordinal') {
      const ordinalIndex = this.ordinalOptions.indexOf(f.patternOrdinal) + 1;
      const weekday = f.patternWeekday.substring(0, 3).toUpperCase();
      return `${minute} ${hour} ? * ${weekday}#${ordinalIndex}`;
    }

    return null;
  }
  private parseCronExpression(cron: string | null | undefined): {
    startTime: string;
    patternChoice: '' | 'day' | 'ordinal';
    patternDay: number | null;
    patternMonthInterval: number | null;
    patternOrdinal: string | null;
    patternWeekday: string | null;
  } {
    const out = {
      startTime: '',
      patternChoice: '' as '' | 'day' | 'ordinal',
      patternDay: null as number | null,
      patternMonthInterval: 1 as number | null,
      patternOrdinal: null as string | null,
      patternWeekday: null as string | null,
    };

    if (!cron) return out;

    const parts = cron.trim().split(/\s+/);
    // expecting Quartz: minute hour dom month dow [year?]
    if (parts.length < 5) return out;

    const minute = Number(parts[0]);
    const hour = Number(parts[1]);

    if (!Number.isNaN(hour) && !Number.isNaN(minute)) {
      out.startTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }

    const dom = parts[2];   // day-of-month or '?'
    const mon = parts[3];   // "1/2" or "*"
    const dow = parts[4];   // "?" or "THU#2"

    // Pattern A: Day-of-month => "m h D 1/M ?"
    if (dom !== '?' && dow === '?') {
      out.patternChoice = 'day';

      const d = Number(dom);
      out.patternDay = Number.isNaN(d) ? null : d;

      const m = (mon || '').split('/');
      if (m.length === 2) {
        const interval = Number(m[1]);
        out.patternMonthInterval = Number.isNaN(interval) ? 1 : interval;
      } else {
        out.patternMonthInterval = 1;
      }

      return out;
    }

    // Pattern B: Ordinal weekday => "m h ? * DOW#N"
    if (dom === '?' && dow.includes('#')) {
      out.patternChoice = 'ordinal';

      const [dow3, ordStr] = dow.split('#');
      const ordNum = Number(ordStr);

      const dowMap: any = {
        SUN: 'Sunday', MON: 'Monday', TUE: 'Tuesday', WED: 'Wednesday',
        THU: 'Thursday', FRI: 'Friday', SAT: 'Saturday',
      };
      out.patternWeekday = dowMap[(dow3 || '').toUpperCase()] ?? null;

      const ordMap: any = { 1: 'first', 2: 'second', 3: 'third', 4: 'fourth' };
      out.patternOrdinal = ordMap[ordNum] ?? null;

      return out;
    }

    return out;
  }

  today: string;

  ngOnInit(): void {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    this.today = `${yyyy}-${mm}-${dd}`;
    this.messageForm = this.fb.group({
      notificationType: ['', Validators.required],
      context: [''],
      messageType: ['', Validators.required],
      selectRecipient: [''],
      calendarType: [''],
      priorityLevel: ['', Validators.required],
      salesChannelChoices: [[]],
      patternChoice: [''],
      patternDay: [10],
      employeeIds: [[]],
      patternMonthInterval: [1],
      patternOrdinal: ['second'],
      patternWeekday: ['Thursday'],
      startDate: ['', Validators.required],
      startTime: [''],
      duration: ['', Validators.required],
      usnSalesChannel: [''],
      usnCommissionPlanId: [''],
    });

    this.messageForm.get('employeeIds')?.valueChanges.subscribe((selected) => {
      console.log('employeeIds changed:', selected);
      this.selectedEmployeesDisplay = this.buildSelectedEmployeesDisplay(selected);
    });

this.messageForm.get('notificationType')?.valueChanges
  .pipe(distinctUntilChanged())
  .subscribe((val: string) => {
    const contextCtrl = this.messageForm.get('context');
    const startTimeCtrl = this.messageForm.get('startTime');

    if (!contextCtrl || !startTimeCtrl) return;

    if (val === 'Dispute Reminder') {
      contextCtrl.setValue('', { emitEvent: false });

      this.setControlEnabled(contextCtrl, false);
      contextCtrl.clearValidators();
      contextCtrl.updateValueAndValidity({ emitEvent: false });

      startTimeCtrl.clearValidators();
      startTimeCtrl.updateValueAndValidity({ emitEvent: false });
    } else {
      this.setControlEnabled(contextCtrl, true);
      contextCtrl.setValidators([Validators.required]);
      contextCtrl.updateValueAndValidity({ emitEvent: false });

      startTimeCtrl.setValidators([Validators.required]);
      startTimeCtrl.updateValueAndValidity({ emitEvent: false });
    }
  });


    const initType = this.messageForm.get('notificationType')?.value;
    if (initType) {
      this.messageForm.get('notificationType')?.setValue(initType);
    }

    this.route.paramMap.subscribe((params) => {
      const configId = params.get('id');
      if (configId) {
        this.isCreateMode = false;
        this.loadExistingConfig(configId);
      }
    });

    this.messageForm
      .get('messageType')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe((value) => {
        const recipientCtrl = this.messageForm.get('selectRecipient');
        const employeeIdsCtrl = this.messageForm.get('employeeIds');
        const usnSalesChannelCtrl = this.messageForm.get('usnSalesChannel');
        const usnCommissionPlanCtrl = this.messageForm.get('usnCommissionPlanId');

        if (!recipientCtrl || !employeeIdsCtrl || !usnSalesChannelCtrl || !usnCommissionPlanCtrl) return;

        // reset shared targeted fields whenever type changes
        usnSalesChannelCtrl.reset('', { emitEvent: false });
        usnCommissionPlanCtrl.reset('', { emitEvent: false });
        employeeIdsCtrl.setValue([], { emitEvent: false });
        this.empIdList = [];
        this.commPlansList = [];

        if (value === 'Broadcast to All Users') {
          this.setControlEnabled(recipientCtrl, false);
          recipientCtrl.reset('', { emitEvent: false });
          recipientCtrl.clearValidators();
          recipientCtrl.updateValueAndValidity({ emitEvent: false });

          usnSalesChannelCtrl.clearValidators();
          usnSalesChannelCtrl.updateValueAndValidity({ emitEvent: false });

          usnCommissionPlanCtrl.clearValidators();
          usnCommissionPlanCtrl.updateValueAndValidity({ emitEvent: false });

          employeeIdsCtrl.clearValidators();
          employeeIdsCtrl.updateValueAndValidity({ emitEvent: false });

          this.targetScope = 1;
          this.recipients = [];
          return;
        }

        if (value === 'Sales Channel-Specific News') {
          this.recipients = [];
          this.setControlEnabled(recipientCtrl, true);
          recipientCtrl.reset('', { emitEvent: false });
          recipientCtrl.setValidators([Validators.required]);
          recipientCtrl.updateValueAndValidity({ emitEvent: false });

          usnSalesChannelCtrl.clearValidators();
          usnSalesChannelCtrl.updateValueAndValidity({ emitEvent: false });

          usnCommissionPlanCtrl.clearValidators();
          usnCommissionPlanCtrl.updateValueAndValidity({ emitEvent: false });

          employeeIdsCtrl.clearValidators();
          employeeIdsCtrl.updateValueAndValidity({ emitEvent: false });

          this.getAllSalesChannelWithCommPlans();
          this.targetScope = 2;
          return;
        }

        if (value === 'Commission Plan Specific') {
          this.setControlEnabled(recipientCtrl, false);
          recipientCtrl.reset('', { emitEvent: false });
          recipientCtrl.clearValidators();
          recipientCtrl.updateValueAndValidity({ emitEvent: false });

          usnSalesChannelCtrl.setValidators([Validators.required]);
          usnSalesChannelCtrl.updateValueAndValidity({ emitEvent: false });

          usnCommissionPlanCtrl.setValidators([Validators.required]);
          usnCommissionPlanCtrl.updateValueAndValidity({ emitEvent: false });

          employeeIdsCtrl.clearValidators();
          employeeIdsCtrl.updateValueAndValidity({ emitEvent: false });

          this.recipients = [];
          this.getAllSalesChannelWithCommPlans();
          this.targetScope = 4;
          return;
        }

        if (value === 'User-Specific News') {
          this.setControlEnabled(recipientCtrl, false);
          recipientCtrl.reset('', { emitEvent: false });
          recipientCtrl.clearValidators();
          recipientCtrl.updateValueAndValidity({ emitEvent: false });

          // employee required, sales channel / comm plan optional
          employeeIdsCtrl.setValidators([Validators.required]);
          employeeIdsCtrl.updateValueAndValidity({ emitEvent: false });

          usnSalesChannelCtrl.clearValidators();
          usnSalesChannelCtrl.updateValueAndValidity({ emitEvent: false });

          usnCommissionPlanCtrl.clearValidators();
          usnCommissionPlanCtrl.updateValueAndValidity({ emitEvent: false });

          this.recipients = [];
          this.getAllSalesChannelWithCommPlans();
          this.targetScope = 3;
          return;
        }

        recipientCtrl.clearValidators();
        recipientCtrl.updateValueAndValidity({ emitEvent: false });
        usnSalesChannelCtrl.clearValidators();
        usnSalesChannelCtrl.updateValueAndValidity({ emitEvent: false });
        usnCommissionPlanCtrl.clearValidators();
        usnCommissionPlanCtrl.updateValueAndValidity({ emitEvent: false });
        employeeIdsCtrl.clearValidators();
        employeeIdsCtrl.updateValueAndValidity({ emitEvent: false });
      });



    const startDateCtrl = this.messageForm.get('startDate');
    const patternChoiceCtrl = this.messageForm.get('patternChoice');

    startDateCtrl?.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((val) => {
        const disableRecurrence = !!val;
        ['patternChoice', 'patternDay', 'patternMonthInterval', 'patternOrdinal', 'patternWeekday']
          .forEach((c) => this.setControlEnabled(this.messageForm.get(c), !disableRecurrence));
      });

    patternChoiceCtrl?.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((val) => {
        const disableStart = !!val;
        this.setControlEnabled(startDateCtrl, !disableStart);
      });

    patternChoiceCtrl?.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((val) => {
        const disableStart = !!val;
        this.setControlEnabled(startDateCtrl, !disableStart);
      });
  }

  private buildSelectedEmployeesDisplay(selected: any): string {
    if (!selected) return '';
    const arr = Array.isArray(selected) ? selected : [selected];
    return arr
      .map((emp: any) => `${emp.empName} - ${emp.sc_emp_id}`)
      .join('\n');
  }

  getAllSalesChannelWithCommPlans() {
    console.log("getAllSalesChannelWithCommPlans called");
    this.commissionService.getAllCommPlans()
      .subscribe((data: CommPlan[]) => {
        this.commData = data;
        this.getSalesChannels();
      }, (errorResponse: HttpErrorResponse) => {
        var msg: any;
        msg = errorResponse;
        this.notificationService.sendNotification(NotificationType.ERROR, msg);
      });

  }

  getAllCommPlans() {
    this.commissionService.getAllCommPlans().subscribe((res: CommPlan[]) => {
      this.commData = res;

      this.commissionPlans = (this.commData ?? [])
        .filter(p => p?.commPlanId != null && (p?.commPlan ?? '').toString().trim() !== '')
        .map(p => ({
          value: Number(p.commPlanId),
          label: (p.commPlan ?? '').toString().trim()
        }))
        .filter((item, idx, arr) => arr.findIndex(x => x.value === item.value) === idx)
        .sort((a, b) => a.label.localeCompare(b.label));

      this.recipients = this.commissionPlans;
    });
  }


  selectedComm() {
    const selectedSalesChannel = this.messageForm.get('usnSalesChannel')?.value;

    this.messageForm.patchValue(
      {
        usnCommissionPlanId: '',
        employeeIds: []
      },
      { emitEvent: false }
    );

    this.selectedEmployeesDisplay = '';
    this.empIdList = [];

    if (selectedSalesChannel) {
      this.getCommPlans(selectedSalesChannel);
    } else {
      this.commPlansList = [];
    }
  }



  getCommPlans(salesChannels: string) {
    console.log("getCommPlans called");
    if (salesChannels.length > 0) {
      this.onlineCalcService.getCommPlans(salesChannels).subscribe(
        (response: CalcRunCtrl[]) => {
          this.commPlansList = response;
          console.log(this.commPlansList);

        }
        , (errorResponse: HttpErrorResponse) => {
          var msg: any;
          msg = errorResponse;
          this.notificationService.sendNotification(NotificationType.ERROR, msg);
        }
      )
    }
    else {
      this.commPlansList = [];
    }
  }

  getCommPlan() {
    const commPlanId = this.messageForm.get('usnCommissionPlanId')?.value;

    this.messageForm.patchValue(
      { employeeIds: [] },
      { emitEvent: false }
    );

    this.selectedEmployeesDisplay = '';
    this.empIdList = [];

    if (commPlanId) {
      const date: string = new Date().toLocaleDateString('en-CA');
      this.getEmpIDList(commPlanId, 0, date);
    }
  }
  getEmpIDList(commPlanId: number, i: number, validFromDt: any, savedEmpIds: string[] = []) {
    console.log("getEmpIDList called");
    console.log(commPlanId);
    console.log(validFromDt);

    this.onlineCalcService.getEmpIdByCommIdList(commPlanId, validFromDt).subscribe(
      (response: CalcRunCtrl[]) => {
        this.empIdList = response || [];

        if (savedEmpIds.length) {
          const selectedEmployees = this.empIdList.filter((emp: any) =>
            savedEmpIds.includes(String(emp.sc_emp_id).split('_')[0])
          );

          this.messageForm.patchValue(
            { employeeIds: selectedEmployees },
            { emitEvent: true }
          );

          this.selectedEmployeesDisplay = this.buildSelectedEmployeesDisplay(selectedEmployees);
        }
      },
      (errorResponse: HttpErrorResponse) => {
        var msg: any;
        msg = errorResponse;
        this.notificationService.sendNotification(NotificationType.ERROR, msg);
      }
    );
  }

  getSalesChannels() {
    console.log("getSalesChannels called");
    console.log("commData length:", this.commData?.length);
    console.log("first row:", this.commData?.[0]);

    const set = new Set<string>();

    for (const comm of this.commData ?? []) {
      const raw = comm?.sales_channel_desc;
      const val = (raw ?? '').toString().trim();
      if (val) set.add(val);
    }

    this.salesChannels2 = Array.from(set).sort((a, b) => a.localeCompare(b));
    this.recipients = this.salesChannels2;
  }

  private loadExistingConfig(configId: string): void {
    this.messageService.getNotificationConfigById(configId).subscribe({
      next: (cfg) => {
        let datePart = '', timePart = '';

        if (cfg.startAt && cfg.triggerType === 1) {
          const dateObj = new Date(cfg.startAt);
          if (!isNaN(dateObj.getTime())) {
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const dd = String(dateObj.getDate()).padStart(2, '0');
            const hh = String(dateObj.getHours()).padStart(2, '0');
            const mi = String(dateObj.getMinutes()).padStart(2, '0');

            datePart = `${yyyy}-${mm}-${dd}`;
            timePart = `${hh}:${mi}`;
          }
        }

        const hasRecurrence = cfg.triggerType === 2 && !!cfg.cronExpression;
        const cronParsed = hasRecurrence ? this.parseCronExpression(cfg.cronExpression) : null;

        const notifTitle = (cfg.notifType === 2) ? 'Dispute Reminder' : 'Simple';
        const msgType = this.getMessageType(cfg);
        const savedEmpIds = (cfg.empId || '')
          .split(',')
          .map((s: string) => s.trim())
          .filter((s: string) => !!s);

        // patch safe fields first
        this.messageForm.patchValue({
          notificationType: notifTitle,
          context: (cfg.messageContent?.title || ''),
          priorityLevel: this.getPriorityLabel(cfg.priority),
          patternChoice: hasRecurrence ? (cronParsed?.patternChoice ?? '') : '',
          patternDay: hasRecurrence ? (cronParsed?.patternDay ?? null) : null,
          patternMonthInterval: hasRecurrence ? (cronParsed?.patternMonthInterval ?? 1) : 1,
          patternOrdinal: hasRecurrence ? (cronParsed?.patternOrdinal ?? null) : null,
          patternWeekday: hasRecurrence ? (cronParsed?.patternWeekday ?? null) : null,
          startDate: hasRecurrence ? '' : datePart,
          startTime: hasRecurrence ? (cronParsed?.startTime ?? '') : timePart,
          duration: (cfg.durationSecs ?? 0) / 86400,
        }, { emitEvent: false });

        this.editingConfigId = cfg.notifConfigId;
        this.isUpdateMode = true;

        // let messageType logic run first
        this.messageForm.get('messageType')?.setValue(msgType, { emitEvent: true });

        setTimeout(() => {
          this.messageForm.patchValue({
            selectRecipient: msgType === 'Sales Channel-Specific News'
              ? (cfg.salesChannel || '')
              : '',
            usnSalesChannel: cfg.salesChannel || '',
            usnCommissionPlanId: cfg.commissionPlanId != null
              ? String(cfg.commissionPlanId)
              : '',
          }, { emitEvent: false });

          if ((msgType === 'Commission Plan Specific' || msgType === 'User-Specific News') && cfg.salesChannel) {
            this.onlineCalcService.getCommPlans(cfg.salesChannel).subscribe(
              (plans: CalcRunCtrl[]) => {
                this.commPlansList = plans || [];

                if (cfg.commissionPlanId != null) {
                  this.messageForm.patchValue(
                    { usnCommissionPlanId: String(cfg.commissionPlanId) },
                    { emitEvent: false }
                  );

                  this.getEmpIDList(Number(cfg.commissionPlanId), 0, '2025-11-14', savedEmpIds);
                }
              },
              (errorResponse: HttpErrorResponse) => {
                var msg: any;
                msg = errorResponse;
                this.notificationService.sendNotification(NotificationType.ERROR, msg);
              }
            );
          }
        }, 0);
      },
    });
  }


  private getMessageType(cfg: any): string {
    if (cfg.targetScope === 1) return 'Broadcast to All Users';
    if (cfg.targetScope === 2) return 'Sales Channel-Specific News';
    if (cfg.targetScope === 3) return 'User-Specific News';
    if (cfg.targetScope === 4) return 'Commission Plan Specific';
    return '';
  }

  private getRecipient(cfg: any): string {
    if (cfg.salesChannel) return cfg.salesChannel;
    if (cfg.commissionPlanId) return String(cfg.commissionPlanId);
    return '';
  }

  private getPriorityLabel(priority: number): string {
    switch (priority) {
      case 1: return 'Info: General Information';
      case 2: return 'Alert: Time-Sensitive';
      case 3: return 'Emergency: Critical / Urgent';
      default: return '';
    }
  }

  onSubmit(): void {
    this.requiredVisible = false;
    this.successVisible = false;

    if (!this.messageForm.valid) {
      this.messageForm.markAllAsTouched();
      this.previewVisible = false; // optional: hide preview if it was open
      this.requiredVisible = true;
      this.requiredMessage = 'Required Fields are not selected';
      setTimeout(() => (this.requiredVisible = false), 3000);
      return;
    }

    if (!this.previewVisible) {
      this.previewVisible = true;
      this.successVisible = false;
      this.previewMessage =
        'Preview: Please make sure all fields are correct. If so, please click Create to generate message.';
      return;
    }

    this.previewVisible = false;
    this.createNotification();
  }

  private createNotification(): void {
    if (this.messageForm.valid) {
      const f0 = this.messageForm.getRawValue();
      const bodyText = f0.context;


      if (f0.patternChoice === 'ordinal' && !f0.startDate) {
        const nextStart = this.getNextOrdinalDate(
          f0.patternWeekday,
          f0.patternOrdinal
        );
        this.messageForm.patchValue({ startDate: nextStart });
      }
      let f = this.messageForm.getRawValue();
      const isDispute = f.notificationType === 'Dispute Reminder';

      if (isDispute && (!f.startTime || !f.startTime.trim())) {
        this.messageForm.patchValue({ startTime: '00:00' }, { emitEvent: false });
        f = this.messageForm.getRawValue();
      }

      const normalizedEmployeeIds =
        Array.isArray(f.employeeIds) && f.employeeIds.length
          ? f.employeeIds
            .map((emp: any) => String(emp.sc_emp_id).split('_')[0])
            .join(',')
          : null;

      const days = Number(f.duration);
      const durationSecs = days * 86400;
      const cron = this.buildCronExpression(f);

      let startAt: string | null = null;
      if (!cron && f.startDate && f.startTime) {
        const [Y, M, D] = f.startDate.split('-').map(Number);
        const [h, m] = f.startTime.split(':').map(Number);
        startAt = new Date(Y, M - 1, D, h, m, 0).toISOString();
      }

      const payload: MessageNotificationConfig = {
        notifType: isDispute ? 2 : 1,
        messageContent: this.buildMessageContent(f.notificationType, f.context),

        sqlMappingId: null,
        targetScope: this.targetScope,
        salesChannel:
          this.targetScope === 2
            ? f.selectRecipient
            : (this.targetScope === 3 || this.targetScope === 4)
              ? f.usnSalesChannel
              : null,

        commissionPlanId:
          this.targetScope === 3 || this.targetScope === 4
            ? (f.usnCommissionPlanId ? Number(f.usnCommissionPlanId) : null)
            : null,

        empId: this.targetScope === 3 ? normalizedEmployeeIds : null,
        triggerType: cron ? 2 : 1,
        cronExpression: cron,
        startAt: startAt,
        nextPopTs: null,
        durationSecs: durationSecs,
        priority: this.getPriorityNumber(f.priorityLevel),
        status: 1,
        createdById: this.messageService.loggedInUserEmpId || '0',
      };



      this.messageService.createNotificationConfig(payload).subscribe({
        next: () => {
          this.reset();
          this.successVisible = true;
          this.previewVisible = false;
          setTimeout(() => (this.successVisible = false), 3000);
        },
      });
    } else {
    }
  }

  private isSelectedStartInPast(date: string, time: string): boolean {
    if (!date || !time) return false;

    const [year, month, day] = date.split('-').map(Number);
    const [hour, minute] = time.split(':').map(Number);

    const selected = new Date(year, month - 1, day, hour, minute, 0);

    if (isNaN(selected.getTime())) return false;

    return selected.getTime() <= new Date().getTime();
  }

  private isPastDateTime(date: string, time: string): boolean {
    if (!date || !time) return false;

    const now = new Date();
    const [year, month, day] = date.split('-').map(Number);
    const [hour, minute] = time.split(':').map(Number);

    const selected = new Date(year, month - 1, day, hour, minute, 0);

    return selected.getTime() < now.getTime();
  }

  onUpdate(): void {
    this.requiredVisible = false;
    this.successVisible = false;

    if (!this.messageForm.valid) {
      this.messageForm.markAllAsTouched();
      this.requiredVisible = true;
      setTimeout(() => (this.requiredVisible = false), 3000);
      return;
    }
    const fCheck = this.messageForm.getRawValue();

    if (!fCheck.patternChoice && fCheck.startDate && fCheck.startTime && this.isSelectedStartInPast(fCheck.startDate, fCheck.startTime)) {
      this.requiredVisible = true;
      this.requiredMessage = 'Selected start time has already passed. Please choose a future time.';
      setTimeout(() => (this.requiredVisible = false), 3000);
      return;
    }
    if (this.messageForm.valid && this.editingConfigId) {
      const f = this.messageForm.getRawValue();
      const isDispute = f.notificationType === 'Dispute Reminder';

      let startAt: string | null = null;
      if (f.startDate && f.startTime) {
        const [Y, M, D] = f.startDate.split('-').map(Number);
        const [h, m] = f.startTime.split(':').map(Number);
        const jsDate = new Date(Y, M - 1, D, h, m, 0);
        if (!isNaN(jsDate.getTime())) startAt = jsDate.toISOString();
      }

      const payload: MessageNotificationConfig = {
        notifConfigId: this.editingConfigId!,
        notifType: isDispute ? 2 : 1,
        messageContent: this.buildMessageContent(f.notificationType, f.context),
        sqlMappingId: null,
        targetScope: this.targetScope,
        salesChannel:
          this.targetScope === 2
            ? f.selectRecipient
            : (this.targetScope === 3 || this.targetScope === 4)
              ? f.usnSalesChannel
              : null,

        commissionPlanId:
          this.targetScope === 3 || this.targetScope === 4
            ? (f.usnCommissionPlanId ? Number(f.usnCommissionPlanId) : null)
            : null,

        empId: this.targetScope === 3 && Array.isArray(f.employeeIds)
          ? f.employeeIds
            .map((emp: any) => String(emp.sc_emp_id).split('_')[0])
            .join(',')
          : null,
        triggerType: f.patternChoice ? 2 : 1,
        cronExpression: f.patternChoice ? this.buildCronExpression(f) : null,
        startAt: !f.patternChoice ? startAt : null,
        nextPopTs: null,
        durationSecs: Number(f.duration) * 86400,
        priority: this.getPriorityNumber(f.priorityLevel),
        status: 1,
        createdById: this.messageService.loggedInUserEmpId || '0',
      };



      this.messageService
        .updateNotificationConfig(this.editingConfigId, payload)
        .subscribe({
          next: () => {
            this.successMessage = 'Updated successfully';
            this.successVisible = true;
            setTimeout(() => (this.successVisible = false), 3000);
          },
        });
    } else {
    }
  }

  private getPriorityNumber(label: string): number {
    if (label.startsWith('Info')) return 1;
    if (label.startsWith('Alert')) return 2;
    if (label.startsWith('Emergency')) return 3;
    return 1;
  }

  reset(): void {
    this.messageForm.reset();
    this.selectedChannels = [];
    this.targetScope = 3;
    this.isCreateMode = false;
    this.recipients = [];
  }

  togglePatternChoice(option: 'day' | 'ordinal'): void {
    const patternChoiceCtrl = this.messageForm.get('patternChoice');
    const patternDayCtrl = this.messageForm.get('patternDay');

    const current = patternChoiceCtrl?.value;

    if (current === option) {
      patternChoiceCtrl?.setValue(null, { emitEvent: true });
      patternDayCtrl?.setValue(null, { emitEvent: true });
    } else {
      patternChoiceCtrl?.setValue(option, { emitEvent: true });

      if (option === 'ordinal') {
        patternDayCtrl?.setValue(null, { emitEvent: true });
      } else if (option === 'day' && !patternDayCtrl?.value) {
        patternDayCtrl?.setValue(10, { emitEvent: true });
      }
    }
  }


  /**
   * Find the next date that matches the given ordinal weekday of the month.
   * @param weekday  e.g. "Monday"
   * @param ordinal  e.g. "second"
   * @returns string like "2025-10-13"
   */
  private getNextOrdinalDate(weekday: string, ordinal: string): string {
    const ordinalMap: any = { first: 1, second: 2, third: 3, fourth: 4 };
    const targetWeekday = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ].indexOf(weekday);
    const ordinalNumber = ordinalMap[ordinal] || 1;

    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const firstOfMonth = new Date(year, month - 1, 1);
    const firstDayOfWeek = firstOfMonth.getDay();

    let offset = targetWeekday - firstDayOfWeek;
    if (offset < 0) offset += 7;

    const dayOfMonth = offset + 1 + (ordinalNumber - 1) * 7;
    const result = new Date(year, month - 1, dayOfMonth);

    const mm = (result.getMonth() + 1).toString().padStart(2, '0');
    const dd = result.getDate().toString().padStart(2, '0');
    return `${result.getFullYear()}-${mm}-${dd}`;
  }
}
